import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SiteEntry {
  siteUrl: string;
  permissionLevel?: string;
}

/** Extract the bare hostname (no scheme, no www, no trailing slash) from any GSC property string. */
function hostnameOf(siteUrl: string): string {
  let v = (siteUrl || "").trim();
  if (v.toLowerCase().startsWith("sc-domain:")) v = v.slice("sc-domain:".length);
  v = v.replace(/^https?:\/\//i, "");
  v = v.replace(/^www\./i, "");
  v = v.replace(/\/.*$/, "");
  return v.toLowerCase();
}

/** Build the set of hostnames that appear as BOTH sc-domain: and URL-prefix variants. */
function hostsWithBothVariants(sites: SiteEntry[]): Set<string> {
  const scHosts = new Set<string>();
  const urlHosts = new Set<string>();
  for (const s of sites) {
    const h = hostnameOf(s.siteUrl);
    if (!h) continue;
    if (s.siteUrl.toLowerCase().startsWith("sc-domain:")) scHosts.add(h);
    else urlHosts.add(h);
  }
  const both = new Set<string>();
  for (const h of scHosts) if (urlHosts.has(h)) both.add(h);
  return both;
}

const GscSelectProperty = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sites, setSites] = useState<SiteEntry[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [clientId, setClientId] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) {
        navigate("/auth");
        return;
      }
      const { data: client } = await supabase
        .from("clients")
        .select("id, website_url, gsc_available_properties, gsc_property")
        .eq("user_id", uid)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (!client) {
        setError("Could not find your client record.");
        setLoading(false);
        return;
      }

      setClientId(client.id);
      setWebsiteUrl((client as any).website_url || null);
      const props = ((client as any).gsc_available_properties || []) as SiteEntry[];

      if (!Array.isArray(props) || props.length === 0) {
        setError("Could not load your GSC properties. Please reconnect.");
        setSites([]);
      } else {
        setSites(props);

        // Auto-select the recommended property:
        // If a hostname appears as BOTH sc-domain: and URL-prefix, pre-select
        // the sc-domain version (covers all subdomains/protocols and is where
        // Google attaches the actual analytics data in most setups).
        const both = hostsWithBothVariants(props);
        const existing = (client as any).gsc_property as string | undefined;
        if (existing) {
          setSelected(existing);
        } else {
          const recommended =
            props.find(
              (p) =>
                p.siteUrl.toLowerCase().startsWith("sc-domain:") &&
                both.has(hostnameOf(p.siteUrl)),
            ) || props[0];
          if (recommended) setSelected(recommended.siteUrl);
        }
      }
      setLoading(false);
    })();
  }, [navigate]);

  const handleConfirm = async () => {
    if (!selected || !clientId) return;
    setSaving(true);
    setError(null);

    // Derive a clean canonical website_url from the chosen GSC property so the
    // dashboard label and the GSC connection can never drift apart again.
    // Examples:
    //   "sc-domain:sandton-locksmith.co.za"     -> "https://sandton-locksmith.co.za"
    //   "https://www.sandton-locksmith.co.za/"  -> "https://sandton-locksmith.co.za"
    const cleanedWebsiteUrl = (() => {
      let v = (selected || "").trim();
      if (v.toLowerCase().startsWith("sc-domain:")) v = v.slice("sc-domain:".length);
      v = v.replace(/^https?:\/\//i, "");
      v = v.replace(/^www\./i, "");
      v = v.replace(/\/+$/g, "");
      return v ? `https://${v}` : "";
    })();

    console.log("[GSC SELECT] syncing website_url:", cleanedWebsiteUrl, "from property:", selected);

    const { error: updErr } = await supabase
      .from("clients")
      .update({
        gsc_property: selected,
        gsc_property_selected: true,
        ...(cleanedWebsiteUrl ? { website_url: cleanedWebsiteUrl } : {}),
      } as any)
      .eq("id", clientId);
    setSaving(false);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    navigate("/dashboard");
  };

  const handleReconnect = () => {
    navigate("/dashboard?gsc=reconnect");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl rounded-2xl border border-border bg-card p-8 shadow-lg">
        <h1 className="text-2xl font-extrabold tracking-tight">Select your Search Console property</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Choose which Google Search Console property OrbitRank should pull data from.
          {websiteUrl ? <> Your website on file is <span className="font-semibold text-foreground">{websiteUrl}</span>.</> : null}
        </p>

        {loading ? (
          <div className="mt-8 text-center text-sm text-muted-foreground">Loading your properties…</div>
        ) : error ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
            <Button onClick={handleReconnect} variant="outline" className="w-full">
              Reconnect Google Search Console
            </Button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {(() => {
              const both = hostsWithBothVariants(sites);
              const selectedIsRecommended =
                !!selected &&
                selected.toLowerCase().startsWith("sc-domain:") &&
                both.has(hostnameOf(selected));
              return (
                <>
                  <Select value={selected} onValueChange={setSelected}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a property…" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((s) => {
                        const isRecommended =
                          s.siteUrl.toLowerCase().startsWith("sc-domain:") &&
                          both.has(hostnameOf(s.siteUrl));
                        return (
                          <SelectItem key={s.siteUrl} value={s.siteUrl}>
                            <span className="flex items-center gap-2">
                              <span>{s.siteUrl}</span>
                              {isRecommended && (
                                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                  Recommended
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {selectedIsRecommended && (
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">Recommended</span> — covers all subdomains and protocols, and is where Google attaches your full traffic data.
                    </p>
                  )}
                </>
              );
            })()}

            <Button
              onClick={handleConfirm}
              disabled={!selected || saving}
              className="w-full"
            >
              {saving ? "Saving…" : "Confirm"}
            </Button>

            <button
              type="button"
              onClick={handleReconnect}
              className="w-full text-xs text-muted-foreground underline hover:text-foreground"
            >
              Don't see your property? Reconnect
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GscSelectProperty;
